import { useState, useEffect, useCallback } from "react";
import { apiGet, fmtDateTime } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, RefreshCcw, ChevronLeft, ChevronRight, Loader2, CheckCircle, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";

const ACTION_TYPES = [
  "all", "CREATE_INVOICE", "POST_INVOICE", "ALLOCATE_INVOICE",
  "CREATE_LANDED_COST", "POST_FREIGHT_INVOICE", "ALLOCATE_RELEASE",
  "CREATE_BOE", "RECONCILE_BOE", "POST_CUSTOMS_TO_SAP",
];

const PAGE_SIZE = 25;

export default function AuditLog() {
  const [logs, setLogs]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [loading, setLoading]     = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [emailSearch, setEmailSearch]   = useState("");
  const [selectedLog, setSelectedLog]   = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE,
        skip: page * PAGE_SIZE,
        ...(actionFilter !== "all" && { action_type: actionFilter }),
        ...(emailSearch && { user_email: emailSearch }),
      });
      const data = await apiGet(`/audit-log?${params}`);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [page, actionFilter, emailSearch]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg px-4 py-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <Input
            value={emailSearch}
            onChange={(e) => { setEmailSearch(e.target.value); setPage(0); }}
            placeholder="Filter by user email…"
            className="pl-8 h-8 text-sm border-slate-300"
            data-testid="email-search-input"
          />
        </div>
        <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
          <SelectTrigger className="w-52 h-8 text-sm border-slate-300" data-testid="action-type-filter">
            <SelectValue placeholder="All action types" />
          </SelectTrigger>
          <SelectContent>
            {ACTION_TYPES.map((a) => (
              <SelectItem key={a} value={a}>{a === "all" ? "All Actions" : a.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={fetchLogs} className="h-8 gap-1.5 text-[12px]" data-testid="refresh-logs-button">
          <RefreshCcw size={12} /> Refresh
        </Button>
        <span className="text-[12px] text-slate-400 font-mono ml-auto">{total} log{total !== 1 ? "s" : ""}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="bg-slate-200 border-b-2 border-slate-300">
                {["Timestamp", "Action", "Entity", "User", "SAP Service", "SAP Doc ID", "Status", ""].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-bold text-slate-700 uppercase text-[11px] tracking-wide whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-300">
              {loading ? (
                <tr><td colSpan={8} className="py-10 text-center">
                  <Loader2 size={18} className="animate-spin mx-auto text-slate-400" />
                </td></tr>
              ) : !logs.length ? (
                <tr><td colSpan={8} className="py-12 text-center text-[13px] text-slate-400">No audit log entries found</td></tr>
              ) : logs.map((log, i) => (
                <tr
                  key={log.log_id || i}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => setSelectedLog(log)}
                  data-testid={`log-row-${log.log_id}`}
                >
                  <td className="px-4 py-3 font-mono text-slate-500 whitespace-nowrap text-[12px]">{fmtDateTime(log.timestamp)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{log.action_type?.replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 font-mono text-[#0052CC] text-[12px]">{log.entity_ref || log.entity_id}</td>
                  <td className="px-4 py-3 text-slate-600 truncate max-w-[120px]">{log.user_email}</td>
                  <td className="px-4 py-3 font-mono text-slate-500 text-[12px] truncate max-w-[140px]">{log.sap_service || "—"}</td>
                  <td className="px-4 py-3 font-mono text-slate-400 text-[12px]">{log.sap_document_id || "—"}</td>
                  <td className="px-4 py-3">
                    {log.status === "success" ? (
                      <div className="flex items-center gap-1 text-emerald-600">
                        <CheckCircle size={12} />
                        <span className="text-[11px] font-mono font-semibold">success</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-red-600">
                        <XCircle size={12} />
                        <span className="text-[11px] font-mono font-semibold">error</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400">
                      <Eye size={12} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
            <p className="text-[12px] text-slate-400">Page {page + 1} of {totalPages}</p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0} className="h-7 w-7 p-0" data-testid="prev-page-btn">
                <ChevronLeft size={13} />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1} className="h-7 w-7 p-0" data-testid="next-page-btn">
                <ChevronRight size={13} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Log detail dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-slate-900 text-[14px]">
              {selectedLog?.action_type?.replace(/_/g, " ")}
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4 mt-2 text-[12px]">
              <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {[
                  ["Entity", selectedLog.entity_ref || selectedLog.entity_id],
                  ["User", selectedLog.user_email],
                  ["Timestamp", fmtDateTime(selectedLog.timestamp)],
                  ["Status", selectedLog.status],
                  ["SAP Service", selectedLog.sap_service || "—"],
                  ["SAP Doc ID", selectedLog.sap_document_id || "—"],
                ].map(([k, v]) => (
                  <>
                    <dt key={`k-${k}`} className="text-slate-400">{k}</dt>
                    <dd key={`v-${k}`} className="font-mono font-medium text-slate-900 break-all">{v}</dd>
                  </>
                ))}
              </dl>

              {selectedLog.error_message && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-700 text-[11px] font-mono">
                  {selectedLog.error_message}
                </div>
              )}

              {selectedLog.response_summary && (
                <div>
                  <p className="text-[10px] font-mono uppercase text-slate-400 mb-1">Response Summary</p>
                  <pre className="bg-slate-50 border border-slate-200 rounded p-3 text-[10px] font-mono text-slate-700 overflow-x-auto whitespace-pre-wrap">
                    {selectedLog.response_summary}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
